/**
 * @fileoverview Forgot password page.
 * Accepts an email address and sends a password reset link via the API.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

/**
 * Forgot password page component.
 * Renders an email input and calls POST /api/auth/forgot-password.
 * Shows a success message after submission regardless of whether the email exists
 * (to prevent email enumeration).
 *
 * @returns {JSX.Element} The forgot password page
 */
function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  /**
   * Handle form submission — sends the forgot-password request.
   *
   * @param {React.FormEvent} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setSubmitting(true);
    try {
      await api.forgotPassword({ email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-card">
          <div className="forgot-password-header">
            <a href="https://cogslog.com" aria-label="COGSLOG home">
              <img src="/logos/stacked/cogslog-stacked-tagline-color.svg" alt="COGSLOG" className="auth-logo" />
            </a>
            <svg
              className="forgot-password-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <h1 className="forgot-password-title">Check Your Email</h1>
            <p className="forgot-password-subtitle">
              If an account exists for <strong>{email}</strong>, we sent a password reset link.
              Please check your inbox.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/login')}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-card">
        <div className="forgot-password-header">
          <a href="https://cogslog.com" aria-label="COGSLOG home">
            <img
              src="/logos/stacked/cogslog-stacked-tagline-color.svg"
              alt="COGSLOG"
              className="auth-logo"
            />
          </a>
          <h1 className="forgot-password-title">Forgot Password</h1>
          <p className="forgot-password-subtitle">
            Enter your email and we will send you a reset link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="forgot-password-form" noValidate>
          {error && (
            <div id="forgot-password-error" className="err-msg" role="alert">
              {error}
            </div>
          )}

          <div className="field">
            <label htmlFor="email" className="label">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
              disabled={submitting}
              autoFocus
              aria-invalid={!!error}
              aria-describedby={error ? 'forgot-password-error' : undefined}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary forgot-password-submit"
            disabled={submitting}
          >
            {submitting ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div className="forgot-password-links">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/login')}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
